import React, { useLayoutEffect, useState } from 'react'

import {
  Alert as AlertComponent,
  Icon,
  palette,
} from '@centivo/react-components'
import styled, { css } from 'styled-components'
import { useHistory, useLocation } from 'react-router-dom'
import { parse, stringify } from 'query-string'

import { Modal, ModalProps } from 'src/refactor/blocks/Modal'
import { ContactForm } from 'src/refactor/forms/contact'
import { Content, ContentSection } from 'src/refactor/structure'

interface ContainerProps {
  titleAlign?: 'center' | 'right'
}

interface ContentProps {
  from: 'home' | 'contact' | 'modal'
}

const Alert = styled(AlertComponent)`
  padding-right: 8px;
  width: 100%;
  margin-bottom: 24px;

  & h2 {
    font-size: 16px;
    margin: 0;
    font-weight: 700;
  }

  & h3 {
    font-size: 12px;
  }
`

const containerStyle = css<ContainerProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  max-width: 27.5rem;
  padding: 0;
  width: 98%;

  & > h2 {
    align-self: flex-start;
    font-size: 1.875rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: ${palette.brand.purple};
    text-align: ${({ titleAlign = 'left' }) => titleAlign};
    width: 100%;
  }
`

const SectionContainer = styled(ContentSection)<ContainerProps>`
  ${containerStyle}
  padding-bottom: 6.25rem;
`

const PageContainer = styled(Content)<ContainerProps>`
  ${containerStyle}
  padding: 0 1.25rem;
`

const ContactContent: React.FC<ContentProps> = ({ from }) => {
  const location = useLocation()
  const history = useHistory()

  const [isSubmitted, setSubmitted] = useState(false)
  const [hasErrors, setErrors] = useState(false)

  useLayoutEffect(() => {
    const {
      contactSubmitted,
      contactFailed,
      from: fromSearch = 'modal',
      ...rest
    } = parse(location.search)

    if (
      from === fromSearch &&
      (contactSubmitted === 'true' || contactFailed === 'true')
    ) {
      setSubmitted(contactSubmitted === 'true')
      setErrors(contactFailed === 'true')

      setTimeout(() => {
        setSubmitted(false)
        setErrors(false)
      }, 10000)

      const hash = from === 'home' ? 'contact' : location.hash

      history.replace({
        ...location,
        search: stringify(rest),
        hash,
      })
    }
  }, [from, history, location])

  return (
    <>
      <h2>Contact us</h2>
      <Alert
        appearance="success"
        title="Success"
        textContent="Your message has been sent. We will respond to you as soon as possible."
        shouldShow={isSubmitted}
      />
      <Alert
        appearance="error"
        title="We're sorry"
        textContent="There was an error trying to send your message. Please try again later."
        shouldShow={hasErrors}
      />
      <ContactForm from={from} />
    </>
  )
}

export const Contact = () => (
  <PageContainer titleAlign="center">
    <ContactContent from="contact" />
  </PageContainer>
)

Contact.Modal = React.memo(function ContactModal(props: ModalProps) {
  const { onRequestClose } = props

  return (
    <Modal {...props}>
      <Icon onClick={onRequestClose} icon="x" />
      <ContactContent from="modal" />
    </Modal>
  )
})

Contact.Section = React.forwardRef<HTMLElement>(function ContactSection(
  props,
  ref
) {
  return (
    <SectionContainer id="contact" ref={ref}>
      <ContactContent from="home" />
    </SectionContainer>
  )
})
